/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { transaction } from 'mobx';
import { getCluGptService } from 'src/clu/core/services/gpt/cluGptService';
import { getCluIntentService } from 'src/clu/core/services/intents/cluIntentService';
import { CluIntentStore } from 'src/clu/core/stores/CluIntentStore';
import { CluProjectStore } from 'src/clu/core/stores/CluProjectStore';
import { cluIntentStoreToIntentSwagger } from 'src/clu/modules/handlers/intents/cluIntentMappers';
import { getCluProjectExportMetaDataSwagger } from 'src/clu/modules/handlers/projects/cluProjectMappers';
import {
    gptPredictedUtteranceStoreToGptPredictedUtteranceSwagger,
    gptPredictedUtteranceSwaggerToGptPredictedUtteranceStore
} from 'src/clu/modules/handlers/utterances/cluUtteranceMappers';
import { HandlerGroupFactoryDeps, HandlerParams } from 'src/core/types/handler';

export type CluIntentHandlerGroup = ReturnType<typeof createCluIntentHandlerGroup>;

export const addCluIntentsHelper = async (intents: Pick<CluIntentStore, 'name' | 'orchestrationInfo'>[], projectStore: CluProjectStore) => {
    const intentsData = [...projectStore.jsonData.intents, ...intents.map(cluIntentStoreToIntentSwagger)];

    await getCluIntentService().addIntent({
        intentsData,
        projectRequiredMetaData: getCluProjectExportMetaDataSwagger(projectStore)
    });

    transaction(() => {
        const newIntentStores = intents.map(i => new CluIntentStore(i.name, { orchestrationInfo: i.orchestrationInfo }));
        projectStore.intentStoreHouse.batchAdd(newIntentStores);
        projectStore.patchJsonData({ intents: intentsData });
    });
};

export const createCluIntentHandlerGroup = (dependencies: HandlerGroupFactoryDeps) => {
    const { rootStore } = dependencies;

    const addCluIntents = async ({ intents }: HandlerParams<{ intents: Pick<CluIntentStore, 'name' | 'orchestrationInfo'>[] }>) =>
        addCluIntentsHelper(intents, rootStore.currentCluProjectStore);

    const updateCluIntents = async ({ intents }: HandlerParams<{ intents: Pick<CluIntentStore, 'name' | 'orchestrationInfo'>[] }>) => {
        const { currentCluProjectStore } = rootStore;
        const intentsData = [
            ...currentCluProjectStore.jsonData.intents.filter(i => intents.every(intent => intent.name !== i.category)),
            ...intents.map(cluIntentStoreToIntentSwagger)
        ];

        await getCluIntentService().addIntent({
            intentsData,
            projectRequiredMetaData: getCluProjectExportMetaDataSwagger(currentCluProjectStore)
        });

        transaction(() => {
            intents.forEach(intent =>
                currentCluProjectStore.intentStoreHouse.findById(intent.name).setOrchestrationInfo(intent.orchestrationInfo)
            );
            currentCluProjectStore.patchJsonData({ intents: intentsData });
        });
    };

    const deleteCluIntents = async ({ intentNames, context }: HandlerParams<{ intentNames: string[] }>) => {
        const { currentCluProjectStore } = rootStore;
        const { name: projectName, gptPredictionsStore, projectType } = currentCluProjectStore;

        const newIntents = currentCluProjectStore.jsonData.intents.filter(c => !intentNames.includes(c.category));
        const newUtterances = currentCluProjectStore.jsonData.utterances.filter(u => !intentNames.includes(u.intent));
        const relatedGeneratedUtterances = gptPredictionsStore.rawUtterances
            .filter(u => intentNames.includes(u.intent))
            .map(gptPredictedUtteranceSwaggerToGptPredictedUtteranceStore);

        await getCluIntentService().deleteIntents({
            intentsData: newIntents,
            referencedExamples: newUtterances,
            projectRequiredMetaData: getCluProjectExportMetaDataSwagger(currentCluProjectStore)
        });

        await getCluGptService({ context }).submitGptDecisions({
            projectName,
            projectKind: projectType,
            utterances: relatedGeneratedUtterances.map(utterance =>
                gptPredictedUtteranceStoreToGptPredictedUtteranceSwagger(utterance, 'Rejected')
            )
        });

        transaction(() => {
            currentCluProjectStore.patchJsonData({ intents: newIntents, utterances: newUtterances });
            currentCluProjectStore.intentStoreHouse.batchRemoveByIds(intentNames);

            gptPredictionsStore.setRawUtterances(gptPredictionsStore.rawUtterances.filter(u => !intentNames.includes(u.intent)));
        });
    };

    return { addCluIntents, deleteCluIntents, updateCluIntents };
};
